import React, { useState, useRef, useEffect } from 'react';
import { X, Save, Bold, Italic, Underline, List, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import jsPDF from 'jspdf';

interface DocumentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File, title: string, description: string, category: string) => void;
  studentName: string;
  fileType: 'private' | 'official';
}

export default function DocumentEditor({ isOpen, onClose, onSave, studentName, fileType }: DocumentEditorProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const editorRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen && editorRef.current) {
      editorRef.current.focus();
    }
  }, [isOpen]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Bitte geben Sie einen Titel ein.');
      return;
    }

    if (!editorRef.current) return;

    setIsSaving(true);

    try {
      const content = editorRef.current.innerHTML;
      const textContent = editorRef.current.innerText;

      // Create HTML file
      const htmlContent = `<!DOCTYPE html>
<html lang="de">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      max-width: 800px;
      margin: 0 auto;
      padding: 20px;
      line-height: 1.6;
    }
    h1 { color: #333; }
    .metadata {
      color: #666;
      font-size: 0.9em;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ddd;
    }
  </style>
</head>
<body>
  <h1>${title}</h1>
  <div class="metadata">
    <p><strong>Schüler:</strong> ${studentName}</p>
    <p><strong>Erstellt am:</strong> ${new Date().toLocaleString('de-DE')}</p>
    ${description ? `<p><strong>Beschreibung:</strong> ${description}</p>` : ''}
    ${category ? `<p><strong>Kategorie:</strong> ${category}</p>` : ''}
  </div>
  <div>
    ${content}
  </div>
</body>
</html>`;

      // Create Blob and File
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.html`;
      const file = new File([blob], fileName, { type: 'text/html' });

      onSave(file, title, description, category);

      // Reset form
      setTitle('');
      setDescription('');
      setCategory('');
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
      alert('Fehler beim Speichern des Dokuments.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAsPDF = async () => {
    if (!title.trim()) {
      alert('Bitte geben Sie einen Titel ein.');
      return;
    }

    if (!editorRef.current) return;

    setIsSaving(true);

    try {
      const textContent = editorRef.current.innerText;
      const htmlContent = editorRef.current.innerHTML;

      // Create PDF
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const maxWidth = pageWidth - 2 * margin;

      // Add title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(title, margin, margin + 10);

      // Add metadata
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      let yPos = margin + 20;
      pdf.text(`Schüler: ${studentName}`, margin, yPos);
      yPos += 5;
      pdf.text(`Erstellt am: ${new Date().toLocaleString('de-DE')}`, margin, yPos);
      yPos += 5;
      if (description) {
        pdf.text(`Beschreibung: ${description}`, margin, yPos);
        yPos += 5;
      }
      if (category) {
        pdf.text(`Kategorie: ${category}`, margin, yPos);
        yPos += 5;
      }

      // Add content (simple text for now, HTML would require html2pdf or similar)
      yPos += 10;
      pdf.setFontSize(12);
      const lines = pdf.splitTextToSize(textContent, maxWidth);
      
      lines.forEach((line: string) => {
        if (yPos > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
        }
        pdf.text(line, margin, yPos);
        yPos += 7;
      });

      // Convert PDF to Blob
      const pdfBlob = pdf.output('blob');
      const fileName = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

      onSave(file, title, description, category);

      // Reset form
      setTitle('');
      setDescription('');
      setCategory('');
      if (editorRef.current) {
        editorRef.current.innerHTML = '';
      }
      onClose();
    } catch (error) {
      console.error('Error saving PDF:', error);
      alert('Fehler beim Speichern des PDFs.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Dokument erstellen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titel <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titel des Dokuments"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kurze Beschreibung (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategorie
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Kategorie (optional)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Formatting Toolbar */}
          <div className="border border-gray-300 rounded-md p-2 bg-gray-50 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => handleFormat('bold')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Fett"
            >
              <Bold className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('italic')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Kursiv"
            >
              <Italic className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('underline')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Unterstrichen"
            >
              <Underline className="h-4 w-4" />
            </button>
            <div className="w-px h-6 bg-gray-300" />
            <button
              type="button"
              onClick={() => handleFormat('insertUnorderedList')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Aufzählung"
            >
              <List className="h-4 w-4" />
            </button>
            <div className="w-px h-6 bg-gray-300" />
            <button
              type="button"
              onClick={() => handleFormat('justifyLeft')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Linksbündig"
            >
              <AlignLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('justifyCenter')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Zentriert"
            >
              <AlignCenter className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('justifyRight')}
              className="p-2 hover:bg-gray-200 rounded transition-colors"
              title="Rechtsbündig"
            >
              <AlignRight className="h-4 w-4" />
            </button>
          </div>

          {/* Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Inhalt
            </label>
            <div
              ref={editorRef}
              contentEditable
              className="w-full min-h-[400px] px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
              }}
              data-placeholder="Beginnen Sie hier mit der Eingabe..."
            />
            <style>{`
              [contenteditable][data-placeholder]:empty:before {
                content: attr(data-placeholder);
                color: #9ca3af;
                pointer-events: none;
              }
            `}</style>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            Dokument für: <strong>{studentName}</strong> ({fileType === 'private' ? 'Private Arbeitsakte' : 'Offizielle Akte'})
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSaveAsPDF}
              disabled={isSaving || !title.trim()}
              className="px-4 py-2 text-white bg-gray-600 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Als PDF speichern
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !title.trim()}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isSaving ? 'Speichere...' : 'Als HTML speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
